import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, type RowAction } from "../ui/data-table";
import { assetColumns } from "./asset-columns";
import { useAuthStore } from "../../stores/auth";
import { useConnectionStore } from "../../stores/connection";
import { Package, Eye, Edit, Trash2 } from "lucide-react";
import type { AssetIndexRow, AssetTableRow } from "../../types/asset-table";
import type { PresenceMap } from "../../types/data-table";

interface AssetTableProps {
  assets: AssetIndexRow[];
  presence?: PresenceMap;
  isLoading?: boolean;
  onEdit?: (assetId: number) => void;
  onDelete?: (assetId: number) => void;
}

export function AssetTable({
  assets,
  presence,
  isLoading = false,
  onEdit,
  onDelete,
}: AssetTableProps) {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const instanceId = useConnectionStore(s => s.instanceId);

  const canEdit = user?.role === "Admin" || user?.role === "Editor";
  const canDelete = user?.role === "Admin";

  const actions = useMemo<RowAction<AssetTableRow>[]>(() => {
    const items: RowAction<AssetTableRow>[] = [
      {
        label: "View Details",
        icon: Eye,
        onClick: (row) => navigate(`/assets/${row.asset_id}`),
      },
    ];

    if (canEdit && onEdit) {
      items.push({
        label: "Edit",
        icon: Edit,
        onClick: (row) => onEdit(row.asset_id),
      });
    }

    if (canDelete && onDelete) {
      items.push({
        label: "Delete",
        icon: Trash2,
        onClick: (row) => onDelete(row.asset_id),
        variant: "destructive",
        separatorBefore: true,
      });
    }

    return items;
  }, [canEdit, canDelete, onEdit, onDelete, navigate]);

  return (
    <DataTable
      columns={assetColumns}
      data={assets}
      getRowId={(row) => String(row.asset_id)}
      isLoading={isLoading}
      emptyIcon={Package}
      emptyTitle="No assets found"
      emptyDescription="No assets match your current filters. Try adjusting your search criteria."
      presence={presence}
      presenceKey={(row) => row.asset_id}
      currentInstanceId={instanceId}
      actions={actions}
      onRowClick={(row) => navigate(`/assets/${row.asset_id}`)}
    />
  );
}
